import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Trash2, KeyRound, AlertTriangle } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import {
  useMyNumberLifecycle, useCollectMyNumber, useDisposeMyNumber, useRunMyNumberSweep,
  type MyNumberLifecycle, type RetentionState,
} from '@/lib/my-number';

const STATE_STYLE: Record<RetentionState, { cls: string }> = {
  not_collected: { cls: 'bg-ink-100 text-ink-500' },
  active:        { cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  scheduled:     { cls: 'bg-blue-50 text-blue-700 border border-blue-200' },
  due:           { cls: 'bg-warn-50 text-warn-700 border border-warn-500/30' },
  disposed:      { cls: 'bg-ink-50 text-ink-400 line-through' },
};

export function MyNumberPage() {
  const { t } = useTranslation();
  const lc = useMyNumberLifecycle();
  const sweep = useRunMyNumberSweep();
  const [collectFor, setCollectFor] = useState<MyNumberLifecycle | null>(null);
  const [disposeFor, setDisposeFor] = useState<MyNumberLifecycle | null>(null);
  const [sweepMsg, setSweepMsg] = useState<string | null>(null);

  const data = lc.data;
  const dueCount = data?.summary.due ?? 0;

  const stateLabel = (s: RetentionState) => t(`myNumber.state_${s}`, {
    defaultValue: { not_collected: 'Not collected', active: 'Active', scheduled: 'Scheduled', due: 'Due for disposal', disposed: 'Disposed' }[s],
  });

  async function runSweep() {
    setSweepMsg(null);
    const r = await sweep.mutateAsync();
    setSweepMsg(t('myNumber.sweepResult', { count: r.disposed, defaultValue: '{{count}} number(s) disposed.' }));
  }

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('myNumber.title', { defaultValue: 'My Number' })}</h1>
            <p className="mt-1 text-sm text-ink-500">{t('myNumber.subtitle', { defaultValue: 'マイナンバー · collection, encrypted storage, and statutory disposal.' })}</p>
          </div>
          <Link to="/app/compliance" className="text-xs font-semibold text-accent-700 hover:text-accent-600">← {t('myNumber.backToCompliance', { defaultValue: 'Compliance' })}</Link>
        </div>

        {/* Retention notice */}
        <div className="mt-5 rounded-xl border border-accent-500/20 bg-accent-50/60 px-4 py-3 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-accent-600 shrink-0 mt-0.5" />
          <p className="text-xs text-accent-900/80">
            {t('myNumber.retentionNote', {
              years: data?.retentionYears ?? 7,
              defaultValue: 'Numbers are AES-256-GCM encrypted per workspace and every access is audit-logged. Under the 番号法, a number is disposed once its retention need ends — scheduled for {{years}} years after termination, then deleted automatically by the daily sweep.',
            })}
          </p>
        </div>

        {/* Summary + sweep */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {data && (['not_collected', 'active', 'scheduled', 'due', 'disposed'] as RetentionState[]).map((s) => (
            <div key={s} className={`rounded-lg px-3 py-2 text-center ${STATE_STYLE[s].cls}`}>
              <p className="num text-lg font-bold leading-none">{data.summary[s]}</p>
              <p className="text-[10px] uppercase tracking-wide font-semibold mt-1">{stateLabel(s)}</p>
            </div>
          ))}
          <div className="flex-1" />
          <button
            onClick={runSweep}
            disabled={sweep.isPending || dueCount === 0}
            className="inline-flex h-9 items-center gap-2 px-3 rounded-lg bg-warn-600 text-white text-xs font-semibold hover:bg-warn-700 disabled:opacity-40 transition"
            title={dueCount === 0 ? t('myNumber.noneDue', { defaultValue: 'Nothing due for disposal' }) : undefined}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {sweep.isPending ? t('myNumber.sweeping', { defaultValue: 'Disposing…' }) : t('myNumber.runSweep', { defaultValue: 'Run disposal sweep' })}
          </button>
        </div>
        {sweepMsg && <p className="mt-2 text-xs text-emerald-600">{sweepMsg}</p>}

        {/* Table */}
        <div className="mt-5 rounded-2xl border border-ink-200 bg-white overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-50 text-ink-600">
              <tr>
                <th className="text-start font-semibold px-4 py-2.5">{t('myNumber.col_employee', { defaultValue: 'Employee' })}</th>
                <th className="text-start font-semibold px-4 py-2.5">{t('myNumber.col_number', { defaultValue: 'Number' })}</th>
                <th className="text-start font-semibold px-4 py-2.5">{t('myNumber.col_purpose', { defaultValue: 'Purpose of use' })}</th>
                <th className="text-start font-semibold px-4 py-2.5">{t('myNumber.col_state', { defaultValue: 'Retention' })}</th>
                <th className="text-end font-semibold px-4 py-2.5">{t('myNumber.col_actions', { defaultValue: 'Actions' })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {lc.isLoading && <tr><td colSpan={5} className="px-4 py-6 text-ink-500">{t('common.loading')}</td></tr>}
              {data?.employees.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-ink-500">{t('myNumber.empty', { defaultValue: 'No JP employees yet.' })}</td></tr>}
              {data?.employees.map((e) => (
                <tr key={e.employeeId} className={`hover:bg-ink-50/40 ${e.state === 'due' ? 'bg-warn-50/30' : ''}`}>
                  <td className="px-4 py-2.5">
                    <Link to={`/app/people/${e.employeeId}`} className="font-medium text-ink-900 hover:text-accent-600 hover:underline">{e.name}</Link>
                    <p className="text-[11px] text-ink-500">{e.employeeCode || '—'} · {t(`status.${e.status}`, { defaultValue: e.status })}</p>
                  </td>
                  <td className="px-4 py-2.5 num text-ink-700">{e.last4 ? `•••• •••• ${e.last4}` : <span className="text-ink-400">—</span>}</td>
                  <td className="px-4 py-2.5 text-ink-600 max-w-[16rem] truncate">{e.purpose || <span className="text-ink-400">—</span>}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide rounded px-2 py-0.5 ${STATE_STYLE[e.state].cls}`}>{stateLabel(e.state)}</span>
                    {e.disposeAfter && e.state !== 'disposed' && <p className="text-[10px] text-ink-400 mt-1">{t('myNumber.disposeAfter', { date: e.disposeAfter, defaultValue: 'dispose after {{date}}' })}</p>}
                  </td>
                  <td className="px-4 py-2.5 text-end whitespace-nowrap">
                    <button onClick={() => setCollectFor(e)} className="inline-flex items-center gap-1 text-xs font-semibold text-accent-700 hover:text-accent-600">
                      <KeyRound className="h-3.5 w-3.5" />{e.collected ? t('myNumber.recollect', { defaultValue: 'Update' }) : t('myNumber.collect', { defaultValue: 'Collect' })}
                    </button>
                    {e.collected && (
                      <button onClick={() => setDisposeFor(e)} className="ms-3 inline-flex items-center gap-1 text-xs font-semibold text-warn-600 hover:text-warn-700">
                        <Trash2 className="h-3.5 w-3.5" />{t('myNumber.dispose', { defaultValue: 'Dispose' })}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {collectFor && <CollectDialog emp={collectFor} onClose={() => setCollectFor(null)} />}
      {disposeFor && <DisposeDialog emp={disposeFor} onClose={() => setDisposeFor(null)} />}
    </AppShell>
  );
}

function CollectDialog({ emp, onClose }: { emp: MyNumberLifecycle; onClose: () => void }) {
  const { t } = useTranslation();
  const collect = useCollectMyNumber();
  const [plaintext, setPlaintext] = useState('');
  const [purpose, setPurpose] = useState(emp.purpose ?? '年末調整・社会保険・給与支払報告');
  const [error, setError] = useState<string | null>(null);
  const digits = plaintext.replace(/\D/g, '');

  async function submit() {
    setError(null);
    if (digits.length !== 12) { setError(t('myNumber.err_digits', { defaultValue: 'My Number must be 12 digits.' })); return; }
    try {
      await collect.mutateAsync({ employeeId: emp.employeeId, plaintext: digits, purpose });
      onClose();
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  return (
    <Modal onClose={onClose} title={t('myNumber.collectTitle', { name: emp.name, defaultValue: 'Collect My Number — {{name}}' })}>
      <label className="block">
        <span className="block text-xs font-medium text-ink-700 mb-1">{t('myNumber.numberLabel', { defaultValue: 'My Number (12 digits)' })}</span>
        <input
          inputMode="numeric" autoComplete="off" value={plaintext}
          onChange={(e) => setPlaintext(e.target.value)} placeholder="1234 5678 9012"
          className="input num tracking-widest"
        />
        <span className="block text-[11px] text-ink-400 mt-1">{digits.length}/12</span>
      </label>
      <label className="block mt-4">
        <span className="block text-xs font-medium text-ink-700 mb-1">{t('myNumber.purposeLabel', { defaultValue: 'Purpose of use (利用目的)' })}</span>
        <input value={purpose} onChange={(e) => setPurpose(e.target.value)} className="input" />
      </label>
      <p className="mt-3 text-[11px] text-ink-400">{t('myNumber.collectNote', { defaultValue: 'Stored encrypted — only the last 4 digits are shown afterward, and every reveal is logged.' })}</p>
      {error && <div className="mt-3 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <Actions onClose={onClose} onSubmit={submit} busy={collect.isPending} label={t('myNumber.save', { defaultValue: 'Save securely' })} />
    </Modal>
  );
}

function DisposeDialog({ emp, onClose }: { emp: MyNumberLifecycle; onClose: () => void }) {
  const { t } = useTranslation();
  const dispose = useDisposeMyNumber();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (reason.trim().length < 5) { setError(t('myNumber.err_reason', { defaultValue: 'A reason of at least 5 characters is required.' })); return; }
    try {
      await dispose.mutateAsync({ employeeId: emp.employeeId, reason: reason.trim() });
      onClose();
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  return (
    <Modal onClose={onClose} title={t('myNumber.disposeTitle', { name: emp.name, defaultValue: 'Dispose My Number — {{name}}' })}>
      <div className="rounded-lg border border-warn-500/30 bg-warn-50 px-3 py-2 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-warn-600 shrink-0 mt-0.5" />
        <p className="text-xs text-warn-700">{t('myNumber.disposeWarn', { defaultValue: 'This permanently deletes the encrypted number and cannot be undone. The action is audit-logged.' })}</p>
      </div>
      <label className="block mt-4">
        <span className="block text-xs font-medium text-ink-700 mb-1">{t('myNumber.reasonLabel', { defaultValue: 'Reason' })}</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} className="input" placeholder={t('myNumber.reasonPlaceholder', { defaultValue: 'Retention period ended' })} />
      </label>
      {error && <div className="mt-3 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <Actions onClose={onClose} onSubmit={submit} busy={dispose.isPending} label={t('myNumber.confirmDispose', { defaultValue: 'Dispose permanently' })} danger />
    </Modal>
  );
}

function Modal({ onClose, title, children }: { onClose: () => void; title: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function Actions({ onClose, onSubmit, busy, label, danger }: { onClose: () => void; onSubmit: () => void; busy: boolean; label: string; danger?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex justify-end gap-2 pt-5">
      <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('common.cancel')}</button>
      <button type="button" onClick={onSubmit} disabled={busy}
        className={`px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50 transition ${danger ? 'bg-warn-600 hover:bg-warn-700' : 'bg-accent-500 hover:bg-accent-600'}`}>
        {busy ? t('common.saving', { defaultValue: 'Saving…' }) : label}
      </button>
    </div>
  );
}
